/**
 * IncomingCallModal — desktop-web incoming-call card (web dialer receive mode, Telnyx only).
 *
 * Rendered by VoiceCallProvider while the call state is 'incoming'. Mount = start alerting
 * (ringtone + browser notification + title flash), unmount = stop, so the alert lifecycle exactly
 * follows the ring. Answer/decline talk straight to the Telnyx web manager — this feature never
 * exists on native or Twilio, so it deliberately bypasses the provider-agnostic facade.
 */

import { useEffect } from 'react';
import { Briefcase, Phone, PhoneOff } from 'lucide-react';
import { telnyxVoiceWebManager } from '@/lib/telnyxVoiceWeb';
import { formatPhoneNumber } from '@/lib/phoneUtils';

/** What the org knows about a ringing call (X- SIP headers): the line being rung, the caller's
 *  contact record, the job they're probably about. Absent/partial = a plainer card. */
export type IncomingContext = {
  line?: { e164: string; name?: string; company?: string };
  contactName?: string;
  job?: { id: string; ref?: string };
};

interface IncomingCallModalProps {
  callerNumber: string;
  context?: IncomingContext;
  /** Makes the browser notification per-call, so a waiting second call doesn't replace this one's. */
  callSid?: string;
}

/**
 * The card's text, shared with the call-waiting banner so both describe a caller identically.
 */
export function deriveIncomingLabels(callerNumber: string, context?: IncomingContext) {
  const line = context?.line;
  const displayNumber = callerNumber ? formatPhoneNumber(callerNumber) : 'Unknown caller';
  // A known caller leads with their name and keeps the number as the subtitle; an unknown one
  // keeps the number as the headline, exactly as before.
  const headline = context?.contactName || displayNumber;
  const subtitle = context?.contactName ? displayNumber : null;
  // "Main Line · Acme Plumbing" — whichever of the two the server could name. The company is
  // omitted for numbers shared across companies (there is no single one to show).
  const lineLabel = [line?.name, line?.company].filter(Boolean).join(' · ');
  // The invoice number is what a human recognizes; the id is the fallback when none was issued.
  const jobLabel = context?.job ? `Job ${context.job.ref ?? context.job.id.slice(0, 8)}` : null;
  return { line, displayNumber, headline, subtitle, lineLabel, jobLabel };
}

/** Body text for the browser notification — the same three facts the card leads with. */
export function incomingNotificationBody(
  labels: ReturnType<typeof deriveIncomingLabels>,
  context?: IncomingContext,
): string {
  const { displayNumber, line, lineLabel, jobLabel } = labels;
  return [
    context?.contactName ? `${displayNumber}` : null,
    line ? `to ${formatPhoneNumber(line.e164)}${lineLabel ? ` (${lineLabel})` : ''}` : null,
    jobLabel,
  ].filter(Boolean).join('\n') || displayNumber;
}

/**
 * Bell-style incoming ring over WebAudio — no audio asset needed. Deliberately NOT the 440+480Hz
 * 2s/4s tone that was here before: that is the North American *ringback*, i.e. exactly what you
 * hear as the CALLER waiting for the other side, which made incoming calls sound outgoing.
 * A ~20Hz warble in the 900–1050Hz band on a double-ring cadence is the "someone is calling you"
 * sound. Autoplay policy can keep the AudioContext suspended until the user has interacted with
 * the page; that's expected — the browser notification and title flash cover the silent case.
 */
function startRingtone(): () => void {
  let ctx: AudioContext | null = null;
  let cadenceTimer: ReturnType<typeof setTimeout> | null = null;
  try {
    ctx = new AudioContext();
    void ctx.resume().catch(() => { /* stays suspended without a prior user gesture */ });

    const ringBurst = (at: number, duration: number) => {
      if (!ctx) return;
      const gain = ctx.createGain();
      // Soft edges — a hard start/stop on a 1kHz tone clicks.
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(0.07, at + 0.02);
      gain.gain.setValueAtTime(0.07, at + duration - 0.03);
      gain.gain.linearRampToValueAtTime(0, at + duration);
      gain.connect(ctx.destination);

      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = 975;
      osc.connect(gain);

      // Square LFO swings the pitch ±75Hz at 20Hz — the "brrring" of a bell interrupter.
      const lfo = ctx.createOscillator();
      lfo.type = 'square';
      lfo.frequency.value = 20;
      const depth = ctx.createGain();
      depth.gain.value = 75;
      lfo.connect(depth);
      depth.connect(osc.frequency);

      osc.start(at); lfo.start(at);
      osc.stop(at + duration); lfo.stop(at + duration);
    };

    // Double ring: 0.4s on / 0.2s off / 0.4s on, then 2s of silence.
    const cadence = () => {
      if (ctx && ctx.state === 'running') {
        ringBurst(ctx.currentTime, 0.4);
        ringBurst(ctx.currentTime + 0.6, 0.4);
      }
      cadenceTimer = setTimeout(cadence, 3000);
    };
    cadence();
  } catch { /* no audio — notification + title flash still alert */ }

  return () => {
    if (cadenceTimer !== null) clearTimeout(cadenceTimer);
    if (ctx) void ctx.close().catch(() => { /* ignore */ });
  };
}

export function IncomingCallModal({ callerNumber, context, callSid }: IncomingCallModalProps) {
  const { line, displayNumber, headline, subtitle, lineLabel, jobLabel } =
    deriveIncomingLabels(callerNumber, context);

  // Ringtone
  useEffect(() => startRingtone(), []);

  // Browser notification (permission is requested when receive mode first enables)
  useEffect(() => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    let notification: Notification | null = null;
    try {
      notification = new Notification(context?.contactName || 'Incoming call', {
        // The notification is what a backgrounded tab actually shows, so it carries the same facts
        // as the card: caller, the line they dialed, and the job.
        body: incomingNotificationBody(
          { line, displayNumber, headline, subtitle, lineLabel, jobLabel }, context),
        // Per-call tag: a same-tag notification REPLACES the previous one, which would silently
        // swallow the alert for a second call ringing while this one is still up.
        tag: `web-dialer-incoming-${callSid ?? 'primary'}`,
        requireInteraction: true,
      });
      notification.onclick = () => { window.focus(); notification?.close(); };
    } catch { /* some platforms throw for page-scoped notifications — ignore */ }
    return () => notification?.close();
    // Primitives, not the `context`/`line` objects: their identity changes on every state emit, and
    // re-running this would tear down and re-pop a requireInteraction notification mid-ring.
  }, [displayNumber, line?.e164, lineLabel, context?.contactName, jobLabel, callSid]);

  // Title flash for background tabs
  useEffect(() => {
    const original = document.title;
    let flip = false;
    const timer = setInterval(() => {
      flip = !flip;
      document.title = flip ? `📞 Incoming call…` : original;
    }, 1000);
    return () => { clearInterval(timer); document.title = original; };
  }, []);

  return (
    <div className="fixed bottom-6 right-6 z-[9999] w-80 rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-3">
        <div className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-30" />
          <Phone className="h-6 w-6 text-green-600 dark:text-green-400" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Incoming call</p>
          <p className="truncate text-lg font-semibold text-gray-900 dark:text-white">{headline}</p>
          {subtitle && (
            <p className="truncate text-xs text-gray-600 dark:text-gray-400">{subtitle}</p>
          )}
          {/* Which line is ringing — the point of the whole card for anyone covering several
              numbers. Rendered only when the provider delivered the headers. */}
          {line && (
            <p className="text-xs text-gray-600 dark:text-gray-400">
              to {formatPhoneNumber(line.e164)}
              {lineLabel ? <span className="text-gray-500 dark:text-gray-500"> · {lineLabel}</span> : null}
            </p>
          )}
        </div>
      </div>
      {/* The caller's open job, so whoever answers has the context before they say hello. Not a
          link: this card renders outside the router, and a mis-click during a ring is worse than
          a copy-able reference. */}
      {jobLabel && (
        <p className="mt-3 inline-flex max-w-full items-center gap-1.5 truncate rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
          <Briefcase className="h-3.5 w-3.5 flex-shrink-0" />
          {jobLabel}
        </p>
      )}
      <div className="mt-4 flex gap-3">
        <button
          onClick={() => telnyxVoiceWebManager.rejectIncoming()}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700"
        >
          <PhoneOff className="h-4 w-4" />
          Decline
        </button>
        <button
          onClick={() => void telnyxVoiceWebManager.answerIncoming()}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-green-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700"
        >
          <Phone className="h-4 w-4" />
          Answer
        </button>
      </div>
    </div>
  );
}
